import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Bold, Highlighter, ImagePlus, Link2, Loader2, RemoveFormatting } from 'lucide-react';
import cx from '../ui/cx';

// Swatches mirror the ==yellow== / !!red!! / ^^blue^^ highlights in the page.
export const HIGHLIGHT_OPTIONS = [
  { marker: '==', name: 'Yellow', swatch: 'text-yellow-500 dark:text-yellow-300' },
  { marker: '!!', name: 'Red', swatch: 'text-red-500 dark:text-red-400' },
  { marker: '^^', name: 'Blue', swatch: 'text-blue-500 dark:text-blue-400' },
];

const GAP = 8; // space between the selection and the toolbar
const EDGE = 8; // minimum distance from the viewport edge
const MOBILE_QUERY = '(max-width: 639px), (pointer: coarse)';

// Keep the textarea selection (and focus) when a toolbar button is pressed.
const keepSelection = (event) => event.preventDefault();

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

function prefersBelow() {
  // Phones show their own copy/paste callout above a selection, so sit below it there.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(MOBILE_QUERY).matches;
  } catch (error) {
    return false;
  }
}

/**
 * Works out where the toolbar goes inside its offset parent. `anchor` is the
 * selection's box in the same coordinate space.
 */
export function computeToolbarPlacement({ anchor, size, parentRect, viewport, preferBelow = false }) {
  const centerX = (anchor.left + anchor.right) / 2;

  // Horizontal: centre on the selection, stay inside the editor and the viewport.
  const viewMin = EDGE - parentRect.left;
  const viewMax = viewport.width - EDGE - parentRect.left - size.width;
  const boxMin = Math.max(4, viewMin);
  const boxMax = Math.min(parentRect.width - size.width - 4, viewMax);
  const left = boxMax >= boxMin
    ? clamp(centerX - size.width / 2, boxMin, boxMax)
    : clamp(centerX - size.width / 2, viewMin, viewMax);

  // Vertical: above the selection (below on phones), flipping when there's no room.
  const aboveTop = anchor.top - GAP - size.height;
  const belowTop = anchor.bottom + GAP;
  const aboveFits = parentRect.top + aboveTop >= EDGE;
  const belowFits = parentRect.top + belowTop + size.height <= viewport.height - EDGE;
  let placement;
  if (preferBelow) placement = belowFits || !aboveFits ? 'below' : 'above';
  else placement = aboveFits || !belowFits ? 'above' : 'below';

  return { left, top: placement === 'above' ? aboveTop : belowTop, placement };
}

function ToolbarButton({ label, onAction, disabled, placement, className, children, buttonRef, tabIndex, onFocus }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={label}
      disabled={disabled}
      tabIndex={tabIndex}
      onFocus={onFocus}
      onMouseDown={keepSelection}
      onClick={onAction}
      className={cx(
        'group relative inline-flex h-9 min-w-[36px] shrink-0 items-center justify-center gap-1.5 rounded-lg px-2 text-[13px] font-medium leading-none text-fg-muted',
        'transition-colors duration-100 hover:bg-surface-2 hover:text-fg active:scale-[0.96]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        'sm:h-8 sm:min-w-[32px] sm:px-1.5',
        className
      )}
    >
      {children}
      <span
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-fg px-2 py-1 text-[11px] font-medium leading-none text-surface opacity-0 shadow-pop transition-opacity duration-100 sm:block',
          'group-hover:opacity-100 group-hover:delay-500 group-focus-visible:opacity-100',
          placement === 'below' ? 'top-full mt-2' : 'bottom-full mb-2'
        )}
      >
        {label}
      </span>
    </button>
  );
}

const Divider = () => <span aria-hidden="true" className="mx-1 h-5 w-px shrink-0 bg-line" />;

/**
 * Floating actions for the current text selection in the notebook editor:
 * bold, highlight colours, clear formatting, link as an observation, and
 * attach images as a sticky note. Presentation only; the page owns behaviour.
 */
export default function SelectionToolbar({
  anchor,
  uploading = false,
  onFormat,
  onClear,
  onLink,
  onAddImage,
  onDismiss,
  editorRef,
}) {
  const toolbarRef = useRef(null);
  const buttonRefs = useRef([]);
  const [position, setPosition] = useState(null);
  const [shown, setShown] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);

  const place = useCallback(() => {
    const el = toolbarRef.current;
    if (!el || !anchor) return;
    const parent = el.offsetParent;
    const parentRect = parent
      ? parent.getBoundingClientRect()
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    setPosition(
      computeToolbarPlacement({
        anchor,
        size: { width: el.offsetWidth, height: el.offsetHeight },
        parentRect,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        preferBelow: prefersBelow(),
      })
    );
  }, [anchor]);

  useLayoutEffect(() => {
    place();
  }, [place, uploading]);

  useEffect(() => {
    // Fade in after the first positioned paint.
    const frame = window.requestAnimationFrame(() => setShown(true));
    window.addEventListener('resize', place);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', place);
    };
  }, [place]);

  // Escape and click-away close the toolbar.
  useEffect(() => {
    if (!onDismiss) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      const focusInside = toolbarRef.current?.contains(document.activeElement);
      onDismiss();
      if (focusInside) editorRef?.current?.focus();
    };
    const handlePointerDown = (event) => {
      const target = event.target;
      if (toolbarRef.current?.contains(target)) return;
      // Presses in the editor start a new selection; the editor handles those.
      if (editorRef?.current && editorRef.current.contains(target)) return;
      onDismiss();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [onDismiss, editorRef]);

  if (!anchor) return null;

  const placement = position?.placement || 'above';
  const items = [
    { key: 'bold', label: 'Bold', onAction: () => onFormat('**'), icon: <Bold className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" /> },
    { key: 'clear', label: 'Clear formatting', onAction: onClear, icon: <RemoveFormatting className="h-4 w-4" aria-hidden="true" /> },
    { divider: true, key: 'd1' },
    ...HIGHLIGHT_OPTIONS.map((option) => ({
      key: `hl-${option.marker}`,
      label: `Highlight ${option.name.toLowerCase()}`,
      onAction: () => onFormat(option.marker),
      className: 'sm:min-w-[28px] sm:px-1',
      icon: (
        <Highlighter
          aria-hidden="true"
          strokeWidth={2.25}
          className={cx('h-4 w-4 transition-transform duration-100 group-hover:scale-110', option.swatch)}
        />
      ),
    })),
    { divider: true, key: 'd2' },
    {
      key: 'link',
      label: 'Link to another page',
      onAction: onLink,
      icon: <Link2 className="h-4 w-4" aria-hidden="true" />,
      text: 'Link',
    },
    {
      key: 'image',
      label: uploading ? 'Uploading image' : 'Add image as sticky note',
      onAction: onAddImage,
      disabled: uploading,
      icon: uploading
        ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        : <ImagePlus className="h-4 w-4" aria-hidden="true" />,
      text: uploading ? 'Uploading…' : 'Image',
    },
  ];
  const buttons = items.filter((item) => !item.divider);

  const moveFocus = (event) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const enabled = buttons.map((item, index) => (item.disabled ? -1 : index)).filter((index) => index >= 0);
    if (enabled.length === 0) return;
    const current = Math.max(0, enabled.indexOf(focusIndex));
    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % enabled.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + enabled.length) % enabled.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = enabled.length - 1;
    setFocusIndex(enabled[next]);
    buttonRefs.current[enabled[next]]?.focus();
  };

  let buttonIndex = -1;
  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label="Selection actions"
      aria-orientation="horizontal"
      data-placement={placement}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={moveFocus}
      className={cx(
        'absolute z-[60] flex max-w-[calc(100vw-16px)] items-center rounded-xl border border-line bg-surface-raised p-1 shadow-pop',
        'transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
        placement === 'below' ? 'origin-top' : 'origin-bottom',
        shown && position ? 'translate-y-0 scale-100 opacity-100' : 'scale-95 opacity-0',
        !shown || !position ? (placement === 'below' ? '-translate-y-1' : 'translate-y-1') : ''
      )}
      style={{
        left: `${Math.round(position ? position.left : anchor.left)}px`,
        top: `${Math.round(position ? position.top : anchor.bottom + GAP)}px`,
      }}
    >
      {items.map((item) => {
        if (item.divider) return <Divider key={item.key} />;
        buttonIndex += 1;
        const index = buttonIndex;
        return (
          <ToolbarButton
            key={item.key}
            label={item.label}
            onAction={item.onAction}
            disabled={item.disabled}
            placement={placement}
            className={item.className}
            tabIndex={index === focusIndex ? 0 : -1}
            onFocus={() => setFocusIndex(index)}
            buttonRef={(node) => {
              buttonRefs.current[index] = node;
            }}
          >
            {item.icon}
            {item.text ? <span className="hidden sm:inline">{item.text}</span> : null}
          </ToolbarButton>
        );
      })}
    </div>
  );
}
